//client/src/pages/ExpertDashboard.tsx - WITH TRUE ANONYMOUS MESSAGING (FIXED)
import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import ExpertCaseFeed from '../components/ExpertCaseFeed';

export default function ExpertDashboard() {
    const [messages, setMessages] = useState<any[]>([]);
    const [expert, setExpert] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'messages' | 'case-feed'>('messages');
    const [selectedMessage, setSelectedMessage] = useState<any>(null);
    const [casePostDetails, setCasePostDetails] = useState<any>(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [replyAttachment, setReplyAttachment] = useState<File | null>(null);
    const [sending, setSending] = useState(false);
    const [anonymousReply, setAnonymousReply] = useState(false);
    const [revealingIdentity, setRevealingIdentity] = useState(false);

    useEffect(() => {
        loadDashboard();

        // Real-time subscription for new messages
        let reloadTimeout: any = null;
        const channel = supabase
            .channel('expert-messages')
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
            }, () => {
                if (reloadTimeout) clearTimeout(reloadTimeout);
                reloadTimeout = setTimeout(() => loadDashboard(), 1000);
            })
            .subscribe();

        // Polling fallback in case realtime isn't enabled
        const pollInterval = setInterval(() => {
            loadDashboard();
        }, 10000);

        return () => {
            supabase.removeChannel(channel);
            clearInterval(pollInterval);
        };
    }, []);

    // Keep selectedMessage (open conversation) in sync when messages update
    useEffect(() => {
        if (!selectedMessage || messages.length === 0) return;

        // Rebuild conversations to find the updated version
        const updatedConvs: any = {};
        messages.forEach((msg: any) => {
            const otherUserId = msg.sender_type === 'expert' ? msg.recipient_id : msg.sender_id;
            const key = `${otherUserId}-${(msg.case_title || 'untitled').toLowerCase().trim()}`;
            if (!updatedConvs[key]) {
                updatedConvs[key] = {
                    key,
                    attorney: msg.attorneys || { name: 'Unknown Attorney', firm_name: 'Unknown Firm', email: null, phone_number: null },
                    case_title: msg.case_title,
                    messages: [],
                    latest: msg.created_at,
                    unread: 0,
                    urgency: msg.urgency,
                    case_type: msg.case_type,
                    hospital_provider: msg.hospital_provider,
                    incident_date: msg.incident_date,
                    has_anonymous_replies: false,
                    identity_revealed: false,
                };
            }
            if (msg.attorneys && updatedConvs[key].attorney.name === 'Unknown Attorney') {
                updatedConvs[key].attorney = msg.attorneys;
            }
            updatedConvs[key].messages.push(msg);
        });

        // Find which conversation the selectedMessage belongs to
        const selectedKey = selectedMessage.key;
        if (selectedKey && updatedConvs[selectedKey]) {
            updatedConvs[selectedKey].messages.sort((a: any, b: any) =>
                new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
            setSelectedMessage(updatedConvs[selectedKey]);
        }
    }, [messages]);

    const loadDashboard = async () => {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session?.user) return;


        // Fetch expert record
        const { data: exp, error: expertError } = await supabase
            .from('experts')
            .select('*')
            .eq('user_id', session.user.id)
            .single();

        if (expertError) {
            console.error('Expert fetch error:', expertError);
            return;
        }
        if (!exp) return;

        setExpert(exp);

        // Fetch messages with enhanced fields
        const { data: msgs, error: msgsError } = await supabase
            .from('messages')
            .select(`
                *,
                attorneys (
                    name,
                    email,
                    phone_number,
                    firm_name
                )
            `)
            .or(`expert_id.eq.${exp.id},sender_id.eq.${session.user.id},recipient_id.eq.${session.user.id}`)

            .order('created_at', { ascending: false });


        if (msgsError) {
            console.error('Messages fetch error:', msgsError);
        } else {
            // All messages where this expert is sender or recipient
            const expertMessages = (msgs || []).map(msg => {
                if (msg.sender_id === session.user.id && msg.sender_type === 'expert' && (msg.is_anonymous || msg.anonymous_sender_id)) {
                    return { ...msg, is_my_anonymous_message: true };
                }
                // Strip attorney identity on anonymous attorney messages
                if (msg.is_anonymous && msg.sender_type === 'attorney' && msg.attorneys) {
                    return {
                        ...msg,
                        attorneys: {
                            ...msg.attorneys,
                            name: 'Anonymous Attorney',
                            email: null,
                            phone_number: null,
                            firm_name: 'Hidden',
                        }
                    };
                }
                return msg;
            });

            // Fetch attorney info separately (Supabase join unreliable with .or())
            const attorneyIds = Array.from(new Set(expertMessages.filter(m => m.attorney_id).map(m => m.attorney_id)));
            let attorneyMap: any = {};
            if (attorneyIds.length > 0) {
                const { data: attorneyData } = await supabase
                    .from('attorneys')
                    .select('id, name, email, phone_number, firm_name')
                    .in('id', attorneyIds);
                if (attorneyData) {
                    attorneyData.forEach((a: any) => { attorneyMap[a.id] = a; });
                }
            }

            // Attach attorney info to each message
            const enrichedMessages = expertMessages.map(msg => {
                let attorneys = msg.attorneys || attorneyMap[msg.attorney_id] || null;
                // Strip attorney identity from anonymous messages
                if (msg.is_anonymous && msg.sender_type === 'attorney' && attorneys) {
                    attorneys = { ...attorneys, name: 'Anonymous Attorney', email: null, phone_number: null, firm_name: null };
                }
                return { ...msg, attorneys };
            });

            setMessages(enrichedMessages);

            // Don't mark as read here - only mark when conversation is opened
        }
    };

    const generateAnonymousId = () => {
        const randomNum = Math.floor(Math.random() * 1000000);
        return `anon-expert-${randomNum}`;
    };

    const getOrCreateAnonymousMapping = async (attorneyId: string, caseTitle: string) => {
        // Check if mapping already exists
        const { data: existing, error: fetchError } = await supabase
            .from('anonymous_reply_mapping')
            .select('*')
            .eq('expert_id', expert.id)
            .eq('attorney_id', attorneyId)
            .eq('case_identifier', caseTitle)
            .single();

        if (existing && !fetchError) {
            return existing.anonymous_expert_id;
        }

        // Create new mapping
        const anonymousId = generateAnonymousId();
        const { data: newMapping, error: insertError } = await supabase
            .from('anonymous_reply_mapping')
            .insert({
                expert_id: expert.id,
                attorney_id: attorneyId,
                case_identifier: caseTitle,
                anonymous_expert_id: anonymousId
            })
            .select()
            .single();

        if (insertError) {
            console.error('Error creating anonymous mapping:', insertError);
            throw new Error('Failed to create anonymous mapping');
        }

        return anonymousId;
    };

    const revealIdentity = async (mappingId: string) => {
        setRevealingIdentity(true);
        try {
            // Update the mapping to revealed
            const { error } = await supabase
                .from('anonymous_reply_mapping')
                .update({
                    revealed: true,
                    revealed_at: new Date().toISOString()
                })
                .eq('id', mappingId);

            if (error) throw error;

            alert('Your identity has been revealed to the attorney. They can now see your contact information.');
            await loadDashboard();
        } catch (error: any) {
            console.error('Error revealing identity:', error);
            alert('Failed to reveal identity: ' + error.message);
        } finally {
            setRevealingIdentity(false);
        }
    };

    const sendReply = async () => {
        if (!replyMessage.trim() || !selectedMessage) return;

        setSending(true);
        try {
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            if (sessionError || !session) {
                throw new Error('Not properly authenticated');
            }


            const firstMsg = selectedMessage.messages?.[0] || selectedMessage;

            // Find the attorney's user_id correctly:
            // 1. From an attorney-sent message's sender_id
            // 2. From an expert-sent message's recipient_id (the expert sent TO the attorney)
            const attorneyMsg = (selectedMessage.messages || []).find((m: any) => m.sender_type === 'attorney');
            const expertMsg = (selectedMessage.messages || []).find((m: any) => m.sender_type === 'expert');

            let attorneyUserId = attorneyMsg?.sender_id || expertMsg?.recipient_id || firstMsg.recipient_id;

            // Safety: make sure we're not sending to ourselves
            if (attorneyUserId === session.user.id) {
                attorneyUserId = attorneyMsg?.recipient_id || expertMsg?.sender_id;
                if (attorneyUserId === session.user.id && firstMsg.attorney_id) {
                    const { data: attRow } = await supabase
                        .from('attorneys')
                        .select('user_id')
                        .eq('id', firstMsg.attorney_id)
                        .single();
                    if (attRow) attorneyUserId = attRow.user_id;
                }
            }

            if (!attorneyUserId) {
                throw new Error('Cannot determine attorney user ID from message data');
            }

            let messageData: any = {
                sender_id: session.user.id,
                recipient_id: attorneyUserId,
                attorney_id: firstMsg.attorney_id,
                sender_type: 'expert' as const,
                message: replyMessage.trim(),
                case_title: (selectedMessage.case_title || firstMsg.case_title),
                read: false,
                urgency: (selectedMessage.urgency || selectedMessage.messages?.[0]?.urgency) || 'standard',
                case_type: (selectedMessage.case_type || selectedMessage.messages?.[0]?.case_type) || 'not_specified',
                hospital_provider: (selectedMessage.hospital_provider || selectedMessage.messages?.[0]?.hospital_provider),
                incident_date: (selectedMessage.incident_date || selectedMessage.messages?.[0]?.incident_date),
                is_conflict_notice: false,
                is_anonymous: anonymousReply
            };

            if (anonymousReply) {
                // Get or create anonymous ID for this conversation
                const anonymousId = await getOrCreateAnonymousMapping(
                    firstMsg.attorney_id,
                    (selectedMessage.case_title || firstMsg.case_title)
                );

                // IMPORTANT: Keep expert_id so the message stays in the conversation thread.
                // The is_anonymous flag + anonymous_sender_id control identity visibility.
                messageData.expert_id = expert.id;
                messageData.anonymous_sender_id = anonymousId;
                // Identity is protected by the is_anonymous flag, no need to append text
            } else {
                messageData.expert_id = expert.id;
                messageData.anonymous_sender_id = null;
            }


            // Upload attachment if provided
            let attachmentUrl = null;
            if (replyAttachment) {
                try {
                    const fileExt = replyAttachment.name.split('.').pop();
                    const fileName = `${session.user.id}-${Date.now()}.${fileExt}`;
                    const { error: uploadError } = await supabase.storage
                        .from('case-documents')
                        .upload(fileName, replyAttachment, { cacheControl: '3600', upsert: false });
                    if (!uploadError) {
                        const { data: urlData } = supabase.storage.from('case-documents').getPublicUrl(fileName);
                        attachmentUrl = urlData.publicUrl;
                    }
                } catch (uploadErr) {
                    console.warn('File upload failed, continuing without attachment');
                }
            }
            if (attachmentUrl) messageData.attachment_url = attachmentUrl;

            const { error } = await supabase
                .from('messages')
                .insert([messageData])
                .select();

            if (error) {
                console.error('Insert error details:', error);
                throw new Error(`Database error: ${error.message}`);
            }

            console.log('Reply sent successfully');

            // Send email notification
            if ((firstMsg.attorneys?.email || selectedMessage.attorney?.email)) {
                try {
                    const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8001').trim();
                    await fetch(`${apiUrl}/notify/message`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            recipient_email: selectedMessage.attorney?.email || '',
                            recipient_name: selectedMessage.attorney?.name || 'Attorney',
                            sender_name: 'Expert',
                            case_title: selectedMessage.case_title,
                            is_anonymous: anonymousReply
                        })
                    });
                } catch (emailErr) {
                    console.log('Email notification failed (non-blocking):', emailErr);
                }
            }

            // Don't close the conversation - let it refresh in place
            setReplyMessage('');
            setAnonymousReply(false);
            setReplyAttachment(null);
            await loadDashboard();
        } catch (error: any) {
            console.error('Error sending reply:', error);
            alert(`Failed to send reply: ${error.message}`);
        } finally {
            setSending(false);
        }
    };

    const getUrgencyBadge = (urgency: string) => {
        const badges = {
            emergency: 'bg-red-100 text-red-800',
            urgent: 'bg-orange-100 text-orange-800',
            standard: 'bg-gray-100 text-gray-800'
        };
        return badges[urgency as keyof typeof badges] || badges.standard;
    };

    if (!expert) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const unreadCount = messages.filter(m => !m.read && m.sender_type === 'attorney').length;

    // Group messages by conversation
    const conversations = messages.reduce((acc: any, msg: any) => {
        const otherUserId = msg.sender_type === 'expert' ? msg.recipient_id : msg.sender_id;
        const key = `${otherUserId}-${(msg.case_title || 'untitled').toLowerCase().trim()}`;
        if (!acc[key]) {
            acc[key] = {
                attorney: msg.attorneys || { name: 'Unknown Attorney', firm_name: 'Unknown Firm', email: null, phone_number: null },
                case_title: msg.case_title,
                messages: [],
                latest: msg.created_at,
                unread: 0,
                urgency: msg.urgency,
                case_type: msg.case_type,
                hospital_provider: msg.hospital_provider,
                incident_date: msg.incident_date,
                has_anonymous_replies: false,
                can_reveal_identity: false,
                mapping_id: null,
                identity_revealed: false,

            };
        }
        acc[key].messages.push(msg);
        // Update attorney info - prefer non-anonymous attorney data
        if (msg.attorneys && msg.sender_type === 'attorney' && !msg.is_anonymous) {
            // Non-anonymous attorney message = revealed identity, always use it
            acc[key].attorney = msg.attorneys;
        } else if (msg.attorneys && !acc[key].attorney?.name || acc[key].attorney?.name === 'Unknown Attorney' || acc[key].attorney?.name === 'Anonymous Attorney') {
            // Fallback: use whatever attorney data we have
            acc[key].attorney = msg.attorneys;
        }
        if (!msg.read && msg.sender_type === 'attorney') {
            acc[key].unread++;
        }
        // Check if expert already sent a non-anonymous message or revealed identity
        if (msg.sender_type === 'expert' && !msg.is_anonymous && !msg.anonymous_sender_id) {
            acc[key].identity_revealed = true;
        }
        if (msg.is_my_anonymous_message) {
            acc[key].has_anonymous_replies = true;
            if (msg.can_reveal) {
                acc[key].can_reveal_identity = true;
                acc[key].mapping_id = msg.mapping_id;
            }
            if (!msg.can_reveal) {
                // Already revealed
                acc[key].identity_revealed = true;
            }
        }
        return acc;
    }, {});

    const conversationList = Object.entries(conversations).map(([key, conv]: [string, any]) => ({
        key,
        ...conv,
        messages: conv.messages.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    })).sort((a: any, b: any) => new Date(b.latest).getTime() - new Date(a.latest).getTime());

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="bg-white rounded-lg shadow p-6 mb-8">
                    <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Welcome, {expert.full_name}</h1>
                    <p className="text-gray-600 mt-2">Manage your attorney inquiries and case consultations</p>

                    {!expert.is_verified && (
                        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 mt-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className="h-8 w-8 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-4">
                                    <h3 className="text-lg font-bold text-yellow-800">Account Under Verification</h3>
                                    <p className="text-sm text-yellow-700 mt-1">
                                        Your expert profile has been submitted and is currently under review. All features will be unlocked once this process is complete. Verification typically takes 24-48 business hours.
                                    </p>
                                    <p className="text-sm text-yellow-600 mt-2">
                                        You'll be able to receive case inquiries, respond to attorneys, and browse the case feed once verified.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {unreadCount > 0 && (
                        <p className="mt-2 text-sm text-blue-600 font-medium">
                            You have {unreadCount} new message{unreadCount !== 1 ? 's' : ''}
                        </p>
                    )}
                </div>

                {/* Main Content - locked if unverified */}
                {!expert.is_verified ? (
                    <div className="bg-white rounded-lg shadow p-12 text-center">
                        <svg className="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">Features Locked</h3>
                        <p className="text-gray-500">Your dashboard features will be available once your account has been verified by our team.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow">
                        {/* Tabs */}
                        <div className="border-b overflow-x-auto">
                            <nav className="flex -mb-px-w-max">
                                <button
                                    onClick={() => setActiveTab('messages')}
                                    className={`py-4 px-6 text-sm font-medium border-b-2 transition ${
                                        activeTab === 'messages'
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Case Inquiries ({conversationList.length})
                                    {unreadCount > 0 && (
                                        <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">{unreadCount}</span>
                                    )}
                                </button>
                                <button
                                    onClick={() => setActiveTab('case-feed')}
                                    className={`py-4 px-6 text-sm font-medium border-b-2 transition ${
                                        activeTab === 'case-feed'
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Case Opportunities
                                </button>
                            </nav>
                        </div>

                        <div className="p-6">
                            {activeTab === 'messages' && (
                                <div>
                                    {conversationList.length === 0 ? (
                                        <p className="text-gray-500 text-center py-8">No case inquiries yet</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {conversationList.map((conv) => (
                                                <div
                                                    key={conv.key}
                                                    data-testid="expert-conversation-row"
                                                    className="border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                                                    onClick={async () => {
                                                        setSelectedMessage(conv);
                                                        setCasePostDetails(null);
                                                        if (conv.case_title) {
                                                            const { data: postData } = await supabase
                                                                .from('case_posts')
                                                                .select('*')
                                                                .eq('title', conv.case_title)
                                                                .maybeSingle();
                                                            if (postData) setCasePostDetails(postData);
                                                        }
                                                        // Mark this conversation's messages as read
                                                        const unreadIds = conv.messages.filter((m: any) => !m.read && m.sender_type === 'attorney').map((m: any) => m.id);
                                                        if (unreadIds.length > 0) {
                                                            await supabase.from('messages').update({ read: true }).in('id', unreadIds);
                                                            loadDashboard();
                                                        }
                                                    }}
                                                >
                                                    <div className="p-4">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div className="flex-1">
                                                                <div className="flex items-center space-x-2">
                                                                    <h4 className="font-semibold text-gray-900">
                                                                        {conv.case_title || 'Untitled Case'}
                                                                    </h4>
                                                                    {conv.unread > 0 && (
                                                                        <span className="px-2 py-1 text-xs bg-blue-600 text-white rounded-full">
                                                                        {conv.unread} new
                                                                    </span>
                                                                    )}
                                                                    {conv.has_anonymous_replies && !conv.identity_revealed && (
                                                                        <span className="px-2 py-1 text-xs bg-purple-600 text-white rounded-full">
                                                                        Anonymous
                                                                    </span>
                                                                    )}
                                                                    {conv.urgency && (
                                                                        <span className={`px-2 py-1 text-xs rounded-full ${getUrgencyBadge(conv.urgency)}`}>
                                                                        {conv.urgency}
                                                                    </span>
                                                                    )}
                                                                </div>
                                                                <p className="text-sm text-gray-600 mt-1">
                                                                    From: {(() => {
                                                                    const revealedMsg = (conv.messages || []).find((m: any) => m.sender_type === 'attorney' && m.is_anonymous === false && m.attorneys?.name && m.attorneys.name !== 'Anonymous Attorney');
                                                                    if (revealedMsg) return `${revealedMsg.attorneys.name} • ${revealedMsg.attorneys.firm_name || ''}`;
                                                                    return 'Anonymous Attorney';
                                                                })()}
                                                                </p>
                                                                {conv.hospital_provider && (
                                                                    <p className="text-sm text-gray-500">
                                                                        Provider: {conv.hospital_provider}
                                                                    </p>
                                                                )}
                                                                <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                                                                    {conv.messages[0]?.message || 'No message content'}
                                                                </p>
                                                                {/* Reveal Identity Option */}
                                                                {conversationList.find(c => c.messages.some((m: any) => m.id === selectedMessage?.id))?.can_reveal_identity && (
                                                                    <div className="border-t pt-4 mb-4">
                                                                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                                                                            <p className="text-sm text-purple-800 mb-3">You are currently anonymous in this conversation. Would you like to reveal your identity to this attorney?</p>
                                                                            <button
                                                                                onClick={() => {
                                                                                    if (window.confirm('Are you ready to reveal your identity to this attorney? This action cannot be undone. Your name, email, and contact details will be shared.')) {
                                                                                        const conv = conversationList.find(c => c.messages.some((m: any) => m.id === selectedMessage?.id));
                                                                                        if (conv?.mapping_id) revealIdentity(conv.mapping_id);
                                                                                    }
                                                                                }}
                                                                                disabled={revealingIdentity}
                                                                                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
                                                                            >
                                                                                {revealingIdentity ? 'Revealing...' : 'Reveal My Identity to This Attorney'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-right ml-4">
                                                                <p className="text-xs text-gray-500">
                                                                    {new Date(conv.latest).toLocaleDateString()}
                                                                </p>
                                                                {conv.case_type && (
                                                                    <span className={`text-xs px-2 py-1 rounded mt-1 inline-block ${
                                                                        conv.case_type === 'defense'
                                                                            ? 'bg-purple-100 text-purple-800'
                                                                            : 'bg-green-100 text-green-800'
                                                                    }`}>
                                                                    {conv.case_type}
                                                                </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Message Detail Modal */}
                                    {selectedMessage && (
                                        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto overscroll-contain" onClick={() => { setSelectedMessage(null); setAnonymousReply(false); }}>
                                            <div className="min-h-full flex items-start sm:items-center justify-center p-4">
                                                <div className="bg-white rounded-lg max-w-4xl w-full my-4 sm:my-8 relative" onClick={e => e.stopPropagation()}>
                                                    <div className="p-6">
                                                        <div className="flex justify-between items-start mb-4">
                                                            <div>
                                                                <h3 className="text-xl font-bold text-gray-900">
                                                                    {selectedMessage.case_title || 'Case Inquiry'}
                                                                </h3>
                                                                <p className="text-sm text-gray-600">
                                                                    From: {(() => {
                                                                    const revealedMsg = (selectedMessage.messages || []).find((m: any) => m.sender_type === 'attorney' && m.is_anonymous === false && m.attorneys?.name);
                                                                    if (revealedMsg) return `${revealedMsg.attorneys.name} • ${revealedMsg.attorneys.firm_name || ''}`;
                                                                    return 'Anonymous Attorney';
                                                                })()}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedMessage(null);
                                                                    setAnonymousReply(false);
                                                                }}
                                                                className="text-gray-400 hover:text-gray-600"
                                                            >
                                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>

                                                        {/* Case Post Details */}
                                                        {casePostDetails && (
                                                            <div className="bg-gray-50 rounded-lg p-4 mb-4 border">
                                                                <h4 className="font-semibold text-gray-900 mb-1">Case Post Details</h4>
                                                                <p className="text-sm text-gray-700 mb-2">{casePostDetails.description}</p>
                                                                <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                                                                    {casePostDetails.specialty_needed && <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{casePostDetails.specialty_needed}</span>}
                                                                    {casePostDetails.case_type && casePostDetails.case_type !== 'not_specified' && <span className="bg-gray-200 px-2 py-1 rounded capitalize">{casePostDetails.case_type.replace('_', ' ')}</span>}
                                                                    {casePostDetails.state_needed && <span className="bg-gray-200 px-2 py-1 rounded">{casePostDetails.state_needed}</span>}
                                                                    {casePostDetails.budget_range && <span className="bg-green-100 text-green-800 px-2 py-1 rounded">{casePostDetails.budget_range}</span>}
                                                                    {casePostDetails.urgency && casePostDetails.urgency !== 'standard' && <span className="bg-red-100 text-red-800 px-2 py-1 rounded capitalize">{casePostDetails.urgency}</span>}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Show all messages in thread */}
                                                        <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                                                            {(selectedMessage.messages || [selectedMessage]).map((msg: any) => (
                                                                <div key={msg.id} className={`p-4 rounded-lg ${msg.sender_type === 'expert' ? 'bg-blue-50 ml-4' : 'bg-gray-50 mr-4'}`}>
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <p className="font-semibold text-gray-900 text-sm">
                                                                            {msg.sender_type === 'expert'
                                                                                ? (msg.is_anonymous || msg.anonymous_sender_id ? 'You (Anonymous)' : 'You')
                                                                                : (() => {
                                                                                    if (msg.is_anonymous) return 'Anonymous Attorney';
                                                                                    if (msg.attorneys?.name) return msg.attorneys.name;
                                                                                    // Look through ALL messages for this attorney's name
                                                                                    const allMsgs = selectedMessage.messages || [];
                                                                                    for (const m2 of allMsgs) {
                                                                                        if (m2.attorneys?.name && m2.sender_type === 'attorney' && !m2.is_anonymous) return m2.attorneys.name;
                                                                                    }
                                                                                    if (selectedMessage.attorney?.name && selectedMessage.attorney.name !== 'Unknown Attorney') return selectedMessage.attorney.name;
                                                                                    return 'Attorney';
                                                                                })()
                                                                            }
                                                                        </p>
                                                                        <p className="text-xs text-gray-500">{new Date(msg.created_at).toLocaleString()}</p>
                                                                    </div>
                                                                    <p className="text-gray-700 whitespace-pre-wrap text-sm">{msg.message}</p>
                                                                    <div className="flex items-center justify-between mt-2">
                                                                        <div>
                                                                            {msg.attachment_url && (
                                                                                <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800">
                                                                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                                                    </svg>
                                                                                    View Attachment
                                                                                </a>
                                                                            )}
                                                                        </div>
                                                                        <button
                                                                            onClick={async (e) => {
                                                                                e.stopPropagation();
                                                                                if (!window.confirm('Report this conversation to support?')) return;
                                                                                try {
                                                                                    const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8001').trim();
                                                                                    const allMsgs = (selectedMessage.messages || []).map((m: any) =>
                                                                                        `[${new Date(m.created_at).toLocaleString()}] ${m.sender_type} (anonymous: ${m.is_anonymous}, sender_id: ${m.sender_id}): ${m.message}`
                                                                                    ).join('\n\n');
                                                                                    const res = await fetch(`${apiUrl}/report/message`, {
                                                                                        method: 'POST',
                                                                                        headers: { 'Content-Type': 'application/json' },
                                                                                        body: JSON.stringify({
                                                                                            reporter_email: expert?.email || '',
                                                                                            reporter_name: expert?.full_name || 'Expert',
                                                                                            case_title: selectedMessage.case_title || '',
                                                                                            messages: allMsgs,
                                                                                            conversation_id: selectedMessage.key || selectedMessage.case_title || ''
                                                                                        })
                                                                                    });
                                                                                    const data = await res.json();
                                                                                    if (data.status === 'already_reported') alert('Already reported.');
                                                                                    else if (data.status === 'reported') alert('Report submitted.');
                                                                                    else alert('Failed to submit report.');
                                                                                } catch { alert('Failed to submit report.'); }
                                                                            }}
                                                                            className="text-gray-300 hover:text-red-500 transition-colors"
                                                                            title="Report this conversation"
                                                                        >
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* Reply Box */}
                                                        <div className="border-t pt-4">
                                                            {/* Anonymous Reply Option - hidden if identity already revealed or contact info request */}
                                                            {!((selectedMessage.messages || []).some((m: any) => m.sender_type === 'expert' && !m.is_anonymous && !m.anonymous_sender_id)) && (
                                                                <div className="mb-4 p-4 bg-yellow-100 border-2 border-yellow-400 rounded-lg shadow-md">
                                                                    <label className="flex items-start space-x-3 cursor-pointer">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={anonymousReply}
                                                                            onChange={(e) => setAnonymousReply(e.target.checked)}
                                                                            className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-2 border-gray-400 rounded cursor-pointer"
                                                                        />
                                                                        <div className="flex-1">
                                                                    <span className="text-base font-bold text-gray-900">
                                                                        🔒 Keep my identity completely private
                                                                    </span>
                                                                            <div>
                                                                                <p className="text-sm text-gray-700 mt-1 font-medium">
                                                                                    ✓ Your name, email, and phone will NOT be visible
                                                                                </p>
                                                                                <p className="text-sm text-gray-700">
                                                                                    ✓ Reply as "Anonymous Expert" with a unique ID
                                                                                </p>
                                                                                <p className="text-sm text-gray-700">
                                                                                    ✓ You can reveal your identity later if you choose
                                                                                </p>
                                                                                <p className="text-xs text-gray-600 mt-2 italic">
                                                                                    This creates a truly anonymous conversation. The attorney cannot see your identity in any way.
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </label>
                                                                </div>
                                                            )}

                                                            <textarea
                                                                value={replyMessage}
                                                                onChange={(e) => setReplyMessage(e.target.value)}
                                                                rows={4}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                                placeholder={anonymousReply ? "Type your reply... (Will be sent completely anonymously)" : "Type your reply..."}
                                                            />

                                                            {/* File attachment */}
                                                            <div className="mt-2 flex items-center gap-3">
                                                                <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 flex items-center">
                                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                                    </svg>
                                                                    Attach File
                                                                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setReplyAttachment(e.target.files?.[0] || null)} />
                                                                </label>
                                                                {replyAttachment && (
                                                                    <span className="text-xs text-green-600 flex items-center">
                                                                    ✓ {replyAttachment.name}
                                                                        <button type="button" onClick={() => setReplyAttachment(null)} className="ml-1 text-red-500 hover:text-red-700">×</button>
                                                                </span>
                                                                )}
                                                            </div>

                                                            <div className="mt-3 flex justify-end">
                                                                <button
                                                                    onClick={sendReply}
                                                                    disabled={sending || !replyMessage.trim()}
                                                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                                                                >
                                                                    {sending ? 'Sending...' : anonymousReply && !selectedMessage?.message?.includes('CONTACT INFO REQUEST') ? 'Send Anonymous Reply' : 'Send Reply'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {activeTab === 'case-feed' && (
                                <ExpertCaseFeed />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}